import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/useAuth";
import { useOnline } from "@/hooks/useOnline";
import { ApiRequestError } from "@/lib/api";
import { strings } from "@/lib/strings";

import logoImg from "@/assets/logo.png";
import { Link } from "react-router-dom";

/** The login screen (docs/08 §8.1). */
export function LoginForm() {
  const { login, verifyTotp, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnline();

  const [step, setStep] = useState<"credentials" | "totp">("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const destination = (location.state as { from?: string } | null)?.from ?? "/workbench";

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (step === "credentials") {
        const result = await login(email, password);
        if (result === "totp") setStep("totp");
        else navigate(destination, { replace: true });
      } else {
        await verifyTotp(code);
        navigate(destination, { replace: true });
      }
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(
          caught.isOffline
            ? strings.errors.network
            : caught.error === "rate_limited"
              ? strings.errors.rateLimited
              : caught.message,
        );
      } else {
        setError(strings.errors.generic);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-slate-50 px-4 py-8">
      <Card className="w-full max-w-sm shadow-md">
        <CardBody className="space-y-4">
          <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
            <Link to="/">
              <img
                src={logoImg}
                alt="EKSUTH Logo"
                className="h-12 w-12 rounded-full border border-emerald-600/30 object-contain shadow-sm hover:scale-105 transition-transform"
              />
            </Link>
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-tight">{strings.app.name}</h1>
              <p className="text-xs text-emerald-800 font-semibold">Hospital Staff Portal</p>
              <p className="text-[11px] text-slate-500">{strings.app.tagline}</p>
            </div>
          </div>

          {sessionExpired && (
            <p role="alert" className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              {strings.auth.sessionExpired}
            </p>
          )}

          {!online && (
            <p className="rounded-md bg-slate-100 p-3 text-sm text-slate-700">
              {strings.auth.offlineNotice}
            </p>
          )}

          <form onSubmit={submit} className="space-y-4">
            {step === "credentials" ? (
              <>
                <Field id="email" label={strings.auth.email} required>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
                <Field id="password" label={strings.auth.password} required>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Field>
              </>
            ) : (
              <Field
                id="totp"
                label={strings.auth.twoFactorCode}
                hint={strings.auth.twoFactorPrompt}
                required
              >
                <Input
                  id="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={8}
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </Field>
            )}

            {error && (
              <p role="alert" className="text-sm text-blocking">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" loading={busy} disabled={!online && step === "credentials"}>
              {step === "credentials" ? strings.auth.signIn : strings.auth.verify}
            </Button>
          </form>

          {step === "credentials" && (
            <p className="text-center text-xs text-slate-500">
              {strings.auth.forgotPassword} Ask the Data Manager to send you a reset
              link.
            </p>
          )}

          <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <Link to="/" className="hover:text-emerald-700 underline transition-colors">
              &larr; About Platform
            </Link>
            <Link to="/docs" className="hover:text-emerald-700 underline transition-colors">
              System Documentation &rarr;
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
